/**
 * The operator→robot input stream: a second SCTP DataChannel ("control" in the wire
 * protocol, PROTOCOL.md §2) carrying `operator-input` frames at 25+ Hz. Unlike the
 * reliable `chat` channel (chat-channel.ts) it is unordered with no retransmits, and it
 * has NO send queue: an input frame that can't go out right now is already stale by the
 * time the channel reopens, so it is dropped, never replayed. Frames are not echoed into
 * the operator log either — at stream rate that would drown every other message.
 */

import type { types as Mediasoup } from "mediasoup-client";

export interface ControlChannel {
  attach: (producer: Mediasoup.DataProducer) => void;
  /** @returns true if sent, false if the channel is not open (the frame is dropped) */
  send: (text: string) => boolean;
  close: () => void;
}

export function createControlChannel({
  appendDiag,
}: {
  appendDiag: (s: string, opts?: { level?: string }) => void;
}): ControlChannel {
  let dataProducer: Mediasoup.DataProducer | null = null;
  let sendErrorLogged = false;

  function attach(producer: Mediasoup.DataProducer) {
    dataProducer = producer;
    sendErrorLogged = false;
  }

  function send(text: string): boolean {
    if (dataProducer?.readyState !== "open") return false;
    try {
      dataProducer.send(text);
      return true;
    } catch (err) {
      // TOCTOU with a closing channel — log once per attached producer, not per frame.
      if (!sendErrorLogged) {
        sendErrorLogged = true;
        appendDiag(`[control] send failed: ${err}`, { level: "warn" });
      }
      return false;
    }
  }

  function close() {
    try {
      if (dataProducer && !dataProducer.closed) dataProducer.close();
    } catch {
      /* ignore */
    }
    dataProducer = null;
  }

  return { attach, send, close };
}
